import { useLayoutEffect, useRef, useState } from "react";
import { Card, Typography } from "@mui/material";
import {
  FILTER_HOVER_CHIP_MAX_WIDTH_SX,
  clampFilterHoverChipPosition
} from "./Functions/clampFilterHoverChipPosition.function";

type MeasuredChipSize = {
  label: string;
  width: number;
  height: number;
};

function readChipSize(node: HTMLElement): { width: number; height: number } | undefined {
  const { width, height } = node.getBoundingClientRect();
  if (width < 1 || height < 1) {
    return undefined;
  }
  return { width, height };
}

function isSameMeasuredSize(
  prev: MeasuredChipSize,
  next: { width: number; height: number },
  label: string
): boolean {
  return (
    prev.label === label &&
    Math.abs(prev.width - next.width) < 0.5 &&
    Math.abs(prev.height - next.height) < 0.5
  );
}

/**
 * Filter match % chip that follows the pointer and stays on-screen using its real size.
 * Stays hidden until measured so the first visible frame is already clamped.
 */
export function FilterCompatibilityHoverChip({
  label,
  mouseX,
  mouseY
}: {
  label: string;
  mouseX: number;
  mouseY: number;
}) {
  const chipRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<MeasuredChipSize | undefined>();

  useLayoutEffect(() => {
    const node = chipRef.current;
    if (!node) {
      return undefined;
    }

    const applySize = () => {
      const size = readChipSize(node);
      if (!size) {
        return;
      }
      setMeasured(prev => {
        if (prev && isSameMeasuredSize(prev, size, label)) {
          return prev;
        }
        return { label, width: size.width, height: size.height };
      });
    };

    applySize();

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(applySize);
    observer.observe(node);
    return () => observer.disconnect();
  }, [label]);

  const chipSize = measured?.label === label ? measured : undefined;
  const isSizeKnown = chipSize != null;

  return (
    <Card
      ref={chipRef}
      data-testid="filter-compatibility-hover-chip"
      aria-hidden={isSizeKnown ? undefined : true}
      sx={{
        px: 1,
        py: 0.5,
        position: "fixed",
        visibility: isSizeKnown ? "visible" : "hidden",
        ...clampFilterHoverChipPosition(
          mouseX,
          mouseY,
          window.innerWidth,
          window.innerHeight,
          chipSize
        ),
        width: "max-content",
        maxWidth: FILTER_HOVER_CHIP_MAX_WIDTH_SX,
        boxShadow: 1
      }}
    >
      <Typography variant="caption" sx={{ lineHeight: 1.25, maxWidth: "100%" }}>
        {label}
      </Typography>
    </Card>
  );
}
